import React from 'react';
import styled from 'styled-components';
import { Icon, Link, Text, Tooltip } from '@amzn/storm-ui-v3';
import { exclamation, infoCircle } from '@amzn/storm-ui-icons-v3';
import { ICellRendererParams } from '@ag-grid-community/core';
import { useTranslation } from 'react-i18next';
import { StyledCellWrapper } from '@urc-ui/components/styles';
import { MigrationStatus, SubscriptionMigrationStatus } from '@urc-ui/store/api/migration/types';
import { useTranslateMapItem } from '@urc-ui/hooks/translate-map';
import { MIGRATION_STATUS_NOT_MIGRATED_TRANSLATION, MIGRATION_STATUS_TRANSLATION_MAP } from '@urc-ui/utils/subscription-utils';
import { getBaseurl } from '@urc-ui/utils/url-utils';

const StyledFailedIcon = styled(Icon)`
  color: ${({ theme }) => theme.palette.red[600]};
  margin-left: ${({ theme }) => theme.spacing.mini};
`;

const StyledInfoIcon = styled(Icon)`
  color: ${({ theme }) => theme.palette.gray[700]};
  margin-left: ${({ theme }) => theme.spacing.mini};
`;

interface MigrationStatusCellValue {
  readonly subscription: {
    readonly id: string;
  };
  readonly migrationStatus?: SubscriptionMigrationStatus;
}

const MigrationStatusCellRenderer = (props: ICellRendererParams) => {
  const { t: translate } = useTranslation();
  const value = props.value as MigrationStatusCellValue | undefined;
  const migrationStatus = value?.migrationStatus;
  const subscriptionId = value?.subscription?.id;

  const notMigratedLabel = translate(MIGRATION_STATUS_NOT_MIGRATED_TRANSLATION.id, MIGRATION_STATUS_NOT_MIGRATED_TRANSLATION.default);
  const mappedLabel = useTranslateMapItem(MIGRATION_STATUS_TRANSLATION_MAP, migrationStatus?.status ?? '');
  const label = migrationStatus?.status ? mappedLabel : notMigratedLabel;

  if (migrationStatus?.status === MigrationStatus.FAILED && migrationStatus.errorDetails) {
    return (
      <StyledCellWrapper>
        <Tooltip
          trigger={
            <span>
              <Text fontSize='small' inline={true}>
                {label}
              </Text>
              <StyledFailedIcon type={exclamation} size='sm' />
            </span>
          }
          message={migrationStatus.errorDetails}
          type={'dark'}
        />
      </StyledCellWrapper>
    );
  }

  if (migrationStatus?.status === MigrationStatus.COMPLETED && subscriptionId) {
    const tooltipMessage = (
      <span>
        {translate('sspa_subscription_migration_completed_status_tooltip', 'This subscription has been migrated to unified reporting.')}
        <br />
        <Link href={`${getBaseurl()}/reporting/history?reportId=${subscriptionId}`} target='_blank' rel='noopener noreferrer'>
          {translate('sspa_subscription_migration_completed_status_tooltip_link', 'View subscription')}
        </Link>
      </span>
    );

    return (
      <StyledCellWrapper>
        <Tooltip
          trigger={
            <span>
              <Text fontSize='small' inline={true}>
                {label}
              </Text>
              <StyledInfoIcon type={infoCircle} size='sm' />
            </span>
          }
          message={tooltipMessage}
        />
      </StyledCellWrapper>
    );
  }

  return (
    <StyledCellWrapper>
      <Text fontSize='small'>{label}</Text>
    </StyledCellWrapper>
  );
};

export { MigrationStatusCellRenderer };
